import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import StarIcon from '@mui/icons-material/Star';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { testimonials } from '../data/testimonials';
import type { Testimonial } from '../types';
const AUTOPLAY_MS = 5200;
export function TestimonialCarousel() {
  const total = testimonials.length;
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef, {
    amount: 0.25
  });
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const goTo = useCallback((index: number) => setActive((index % total + total) % total), [total]);
  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);
  useEffect(() => {
    // El autoplay solo corre cuando el carrusel está en pantalla.
    if (paused || !inView) return;
    const id = window.setInterval(() => setActive(current => (current + 1) % total), AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [paused, inView, total]);
  const offsetOf = (index: number) => {
    let distance = index - active;
    if (distance > total / 2) distance -= total;
    if (distance < -total / 2) distance += total;
    return distance;
  };
  return <div ref={containerRef} role="group" aria-roledescription="carrusel" aria-label="Testimonios de clientes" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)} data-id="element-370" data-mp="2bdp6l8e9w321">
      <div className="relative h-[480px] select-none sm:h-[400px] [perspective:1600px]" data-id="element-371" data-mp="4mppmd87qkyr">
        {testimonials.map((testimonial, index) => {
        const distance = offsetOf(index);
        const isActive = distance === 0;
        const visible = Math.abs(distance) <= 1;
        return <div key={testimonial.id} className="absolute inset-0 flex justify-center" style={{
          zIndex: 20 - Math.abs(distance),
          pointerEvents: isActive ? 'auto' : 'none',
          display: Math.abs(distance) <= 1 ? 'flex' : 'none'
        }} aria-hidden={!isActive} data-id="element-372" data-mp="1p5tkzmbfrspz" data-mpi={index}>
              <motion.div className="h-full w-[88%] cursor-grab active:cursor-grabbing sm:w-[68%] lg:w-[56%]" animate={{
            x: `${distance * 66}%`,
            scale: isActive ? 1 : 0.88,
            rotateY: -distance * 14,
            opacity: visible ? isActive ? 1 : 0.4 : 0
          }} transition={{
            duration: 0.45,
            ease: [0.16, 1, 0.3, 1]
          }} drag={isActive ? 'x' : false} dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.15} onDragEnd={(_, info) => {
            if (info.offset.x < -70) next();else if (info.offset.x > 70) prev();
          }} data-id="element-373" data-mp="28rg39q2m9743">
                <TestimonialSlide testimonial={testimonial} active={isActive} data-id="element-374" data-mp="b4dyqvffjg9p" />
              </motion.div>
            </div>;
      })}
      </div>

      <div className="mt-8 flex items-center justify-center gap-5" data-id="element-375" data-mp="ku70l4prsh0r">
        <CarouselButton label="Testimonio anterior" onClick={prev} data-id="element-376" data-mp="kwjl9pmwx5hi">
          <ChevronLeftIcon style={{
          fontSize: 22
        }} data-id="element-377" data-mp="g2yq05cpepm0" />
        </CarouselButton>

        <div className="flex items-center gap-2" data-id="element-378" data-mp="3oiqtspm0z193">
          {testimonials.map((testimonial, index) => <button key={testimonial.id} type="button" onClick={() => goTo(index)} aria-label={`Ver testimonio de ${testimonial.name}`} aria-current={index === active} className={`h-2 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 ${index === active ? 'w-8 bg-brand-blue' : 'w-2 bg-brand-blue/25 hover:bg-brand-blue/50'}`} data-id="element-379" data-mp="3jje7c5z2fw71" data-mpi={index} />)}
        </div>

        <CarouselButton label="Siguiente testimonio" onClick={next} data-id="element-380" data-mp="3ahboph65onkp">
          <ChevronRightIcon style={{
          fontSize: 22
        }} data-id="element-381" data-mp="3ejqb2b91a5gq" />
        </CarouselButton>
      </div>

      <p className="sr-only" aria-live="polite" data-id="element-382" data-mp="3jj2xiuvztais">
        Testimonio {active + 1} de {total}: {testimonials[active].name}
      </p>
    </div>;
}
function CarouselButton({
  label,
  onClick,
  children
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} aria-label={label} className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-line bg-white text-brand-blue shadow-card transition hover:-translate-y-0.5 hover:border-brand-blue hover:bg-brand-blue hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2" data-id="element-383" data-mp="1hlivsj7onzj7">
      {children}
    </button>;
}
function TestimonialSlide({
  testimonial,
  active
}: {
  testimonial: Testimonial;
  active: boolean;
}) {
  return <figure className={`relative flex h-full flex-col overflow-hidden rounded-3xl border-2 bg-white p-8 transition-shadow duration-500 sm:p-10 ${active ? 'border-brand-blue/25 shadow-brand-lg' : 'border-line shadow-card'}`} data-id="element-384" data-mp="1vjh87j2g1lw2">
      <span aria-hidden="true" className="pointer-events-none absolute -right-4 -top-6 text-brand-blue/[0.07]" data-id="element-385" data-mp="f83g88ueidrk">
        <FormatQuoteIcon style={{
        fontSize: 200
      }} data-id="element-386" data-mp="b8344uuryeno" />
      </span>

      <div className="relative mb-5 flex items-center justify-between gap-3" data-id="element-387" data-mp="co731qynoyhr">
        <div className="flex gap-1 text-brand-yellow" aria-label="5 de 5 estrellas" data-id="element-388" data-mp="hm8lc9o9cts6">
          {Array.from({
          length: 5
        }).map((_, index) => <StarIcon key={index} style={{
          fontSize: 22
        }} data-id="element-389" data-mp="7nj48j46rdcz" data-mpi={index} />)}
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-brand-green/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#14803D]" data-id="element-390" data-mp="3q1sdt6h3u866">
          <VerifiedIcon style={{
          fontSize: 14
        }} data-id="element-391" data-mp="3uu208cj0gq3p" />
          Cliente verificado
        </span>
      </div>

      <blockquote className="relative font-display text-[22px] font-bold leading-snug text-ink sm:text-[26px]" data-id="element-392" data-mp="3g55red94r921">
        “{testimonial.quote}”
      </blockquote>

      <div className="relative mt-5 flex flex-wrap gap-2" data-id="element-393" data-mp="372o5y11aukg3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-green/10 px-3.5 py-1.5 text-[13px] font-bold text-[#14803D]" data-id="element-394" data-mp="375adw1s3r1yu">
          <TrendingUpIcon style={{
          fontSize: 16
        }} data-id="element-395" data-mp="2sffssuofk51h" />
          {testimonial.metric}
        </span>
        <span className="rounded-full bg-brand-blue/10 px-3.5 py-1.5 text-[13px] font-semibold text-brand-blue" data-id="element-396" data-mp="2nhwfbwzk3wal">
          {testimonial.packageLabel}
        </span>
      </div>

      <figcaption className="relative mt-auto flex items-center gap-4 border-t border-line pt-6" data-id="element-397" data-mp="2wi2l0wp0jrz7">
        <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,#1B4DCC,#FFD500)] font-display text-lg font-extrabold text-white shadow-glow-blue" data-id="element-398" data-mp="3betq9mldbxwq">
          {testimonial.initials}
        </span>
        <span data-id="element-399" data-mp="3k6viwyp749wg">
          <span className="block font-display text-base font-extrabold text-ink" data-id="element-400" data-mp="3k9hquzg00rf7">
            {testimonial.name}
          </span>
          <span className="block text-sm font-semibold text-brand-blue" data-id="element-401" data-mp="3u5bcr9flxx9n">
            {testimonial.role}
          </span>
        </span>
      </figcaption>
    </figure>;
}